// ###### DEPENDENCY GRAPH
// Define dependencies between stores for sync ordering

// ##### DEPENDENCY DEFINITIONS
// Format: { storeName: [dependencies] }
// Dependencies must be synced BEFORE the dependent store
export const DEPENDENCY_GRAPH = {
  // Clusters have no dependencies
  'notepad_clusters': [],
  
  // Notes depend on clusters (cluster_id must exist)
  'notes': ['notepad_clusters'],
  
  // Todo has no dependencies
  'todo': [],
  
  // Todo items depend on todo (todo_id must exist)
  'todo_item': ['todo'],

  'planner_projects': [],
  'planner_project_columns': ['planner_projects'],
  'planner_project_cards': ['planner_projects', 'planner_project_columns'],
  
  // Trips have no dependencies
  'trips': [],
  
  // Places depend on trips (trip_id must exist)
  'places': ['trips'],
  
  // Flow accounts have no dependencies
  'flow_accounts': [],
  
  // Flow accounts history depends on flow accounts (account_id must exist)
  'flow_accounts_history': ['flow_accounts'],
  
  // Flow categories have no dependencies
  'flow_categories': [],
  
  // Flows depend on flow categories (categ_l0, categ_l1, categ_l2 should exist)
  'flows': ['flow_categories'],
  
  // Budget plans have no dependencies
  'budget_plans': [],

  // Budget plan items depend on budget plans (plan_id must exist)
  'budget_plan_items': ['budget_plans'],

  // Health records have no dependencies
  'health_records': []
};

// ##### RELATIONSHIP FIELDS BY STORE
// Define which fields in each store reference other stores
export const RELATIONSHIP_FIELDS = {
  'notes': ['cluster_id'],
  'todo_item': ['todo_id'],
  'planner_project_columns': ['project'],
  'planner_project_cards': ['project', 'column'],
  'places': ['trip_id'],
  'flow_accounts_history': ['account_id'],
  'budget_plan_items': ['plan_id'],
  'flows': ['categ_l0', 'categ_l1', 'categ_l2'] // These are strings, not IDs, but still dependencies
};

// ##### GET DEPENDENCIES FOR STORE
export function getDependencies(storeName) {
  return DEPENDENCY_GRAPH[storeName] || [];
}

// ##### GET ALL DEPENDENCIES RECURSIVELY
export function getAllDependencies(storeName, visited = new Set()) {
  if (visited.has(storeName)) {
    return []; // Circular dependency protection
  }
  
  visited.add(storeName);
  const dependencies = DEPENDENCY_GRAPH[storeName] || [];
  const allDeps = [...dependencies];
  
  for (const dep of dependencies) {
    const subDeps = getAllDependencies(dep, visited);
    allDeps.push(...subDeps);
  }
  
  return [...new Set(allDeps)]; // Remove duplicates
}

// ##### TOPOLOGICAL SORT - Order stores by dependencies
export function topologicalSort(stores) {
  const sorted = [];
  const visited = new Set();
  const visiting = new Set();
  
  function visit(storeName) {
    if (visiting.has(storeName)) {
      console.warn(`Circular dependency detected involving ${storeName}`);
      return; // Circular dependency
    }
    
    if (visited.has(storeName)) {
      return; // Already processed
    }
    
    visiting.add(storeName);
    
    // Visit dependencies first
    const deps = DEPENDENCY_GRAPH[storeName] || [];
    for (const dep of deps) {
      if (stores.includes(dep)) {
        visit(dep);
      }
    }
    
    visiting.delete(storeName);
    visited.add(storeName);
    sorted.push(storeName);
  }
  
  for (const store of stores) {
    if (!visited.has(store)) {
      visit(store);
    }
  }
  
  return sorted;
}

// ##### GET RELATIONSHIP FIELDS FOR STORE
export function getRelationshipFields(storeName) {
  return RELATIONSHIP_FIELDS[storeName] || [];
}

export default {
  DEPENDENCY_GRAPH,
  RELATIONSHIP_FIELDS,
  getDependencies,
  getAllDependencies,
  topologicalSort,
  getRelationshipFields
};
